import React, {useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Modal, Popconfirm, Select, Table, message} from "antd";
import {EditOutlined, ExportOutlined, InboxOutlined, PlusOutlined} from "@ant-design/icons";
import {ApiApartmentsService, ApiUserSettingsService} from "../../../../../shared/api";
import {SectionToolbar} from "../../components/SectionToolbar/SectionToolbar";
import {ApartmentEditor} from "./ApartmentEditor";
import {useCanEdit} from "../../../sectionAccess";
import {loadComplexes, loadReferences} from "./apartmentReferences";
import {AllFiltersDrawer, FiltersPanel, TickedSelect, kindsOf, toParams} from "./ApartmentFilters";
import {ColumnsButton, ColumnsDrawer, STANDARD_COLUMNS, catalogueOf, fieldsOf, renameOldKeys, sortKeyOf, tableColumns} from "./apartmentColumns";
import {BulkEditModal, EDIT_KINDS, flatsText} from "./BulkEdit";

// «Статус»: one or several, by tick boxes (owner 03.11.2026)
const STATUS_OPTIONS = ['Свободно', 'Забронировано', 'Продано', 'Скрыта'].map((s) => ({value: s, label: s}))
// «Архив»: the flats ECI old has moved out of its working table (backend ftp-node service/eciOldArchive.js), not listed
// unless chosen here (the owner, 28.10: «архив по умолчанию бы не показывался — нужно было бы отдельно в фильтре выбрать»)
const ARCHIVE_OPTIONS = [
  {value: 'without', label: 'Без архива'},
  {value: 'only', label: 'Только архив'},
  {value: 'all', label: 'С архивом'},
]
// ECI old's «Запас» (its filter button): sold or withdrawn flats, which no other list shows; the portal's filter «stock»
const STOCK_OPTIONS = [
  {value: 'without', label: 'Без Запаса'},
  {value: 'only', label: 'Только Запас'},
]
const SORT_PRESETS = [
  {value: 'id', label: 'Сначала новые'},
  {value: 'updated', label: 'Недавно изменённые'},
  {value: 'price', label: 'Дешевле'},
]

// the employee's table, kept on the server ({ columns }); the columns chosen in this browser before 27.10
// move there on the first visit
const SETTINGS_KEY = 'admin.flats.table'
const LOCAL_KEY = 'eci-admin-flat-columns'
const readLocal = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LOCAL_KEY))
    return Array.isArray(saved) && saved.length ? renameOldKeys(saved) : null
  } catch (e) {
    return null
  }
}
const writeLocal = (columns) => {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(columns))
  } catch (e) {
    // private mode or blocked storage: the choice lasts until the page is reloaded
  }
}

// a cell is changed in place, but not these: the card checks or works them out with others
const CARD_ONLY = ['installmentSchedule', 'windowsDirection', 'address', 'latitude', 'longitude', 'coordsArrows']

// the row takes the saved values of its columns (and the ones ECI old's companions changed)
const mergeSaved = (row, saved) => {
  const next = {...row}
  Object.keys(row).forEach((key) => {
    if (key in saved) next[key] = saved[key]
  })
  return next
}

// Flats: the portal's filters, the list's own (status, source, sort), the table with its columns, cells changed in
// place, «Массовая правка», and the editor
export const ApartmentsSection = () => {
  const canEdit = useCanEdit()
  const [filters, setFilters] = useState({q: '', status: undefined, source: undefined, archive: undefined, sort: 'id'})
  // the portal's filters: what this user may use, their options, the chosen values
  const [filterConfig, setFilterConfig] = useState(null)
  const [facets, setFacets] = useState({})
  const [portalFilters, setPortalFilters] = useState({})
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState({current: 1, size: 100})
  // the employee's settings and the form description (the columns are its fields): the list waits for both
  const [settings, setSettings] = useState({columns: STANDARD_COLUMNS})
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [references, setReferences] = useState(null)
  const [referencesDone, setReferencesDone] = useState(false)
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [data, setData] = useState({items: [], total: 0})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [complexes, setComplexes] = useState([])
  // the flat open in the editor: an id, 'new' or null
  const [editing, setEditing] = useState(null)
  // the cell being changed: { id, field }
  const [editingCell, setEditingCell] = useState(null)
  const [savingCell, setSavingCell] = useState(false)
  const [selected, setSelected] = useState([])
  const [bulkOpen, setBulkOpen] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  // one flat found (an ID, a flat number): its «Аналоги в ЖК» and «Аналоги поблизости» under the table, as on the portal and
  // on ECI old (the owner, 05.11.2026: «Аналоги при поиске по ID — в админке тоже нужно так сделать»)
  const [analogs, setAnalogs] = useState(null)
  const timer = useRef(null)
  const saveTimer = useRef(null)
  const clickTimer = useRef(null)
  const requestId = useRef(0)

  const kinds = useMemo(() => kindsOf(filterConfig), [filterConfig])
  const catalogue = useMemo(() => catalogueOf(references && references.meta, references), [references])
  const fields = useMemo(() => fieldsOf(settings.columns, catalogue), [settings.columns, catalogue])

  useEffect(() => {
    loadComplexes().then(setComplexes).catch(() => {})
    loadReferences().then(setReferences).catch(() => {}).finally(() => setReferencesDone(true))
    ApiApartmentsService.fetchFilterConfig().then(({data: result}) => setFilterConfig(result)).catch(() => {})
    ApiApartmentsService.fetchFilterOptions().then(({data: result}) => setFacets(result.facets || {})).catch(() => {})
    ApiUserSettingsService.get(SETTINGS_KEY)
      .then(({data: result}) => {
        const saved = result && result.value
        if (saved && Array.isArray(saved.columns)) {
          setSettings({columns: renameOldKeys(saved.columns)})
          return
        }
        const local = readLocal()
        if (local) {
          setSettings({columns: local})
          ApiUserSettingsService.set(SETTINGS_KEY, {columns: local}).catch(() => {})
        }
      })
      .catch(() => {
        const local = readLocal()
        if (local) setSettings({columns: local})
      })
      .finally(() => setSettingsLoaded(true))
    return () => {
      clearTimeout(timer.current)
      clearTimeout(clickTimer.current)
    }
  }, [])

  const listParams = () => ({
    ...toParams(portalFilters, kinds),
    q: filters.q || undefined,
    status: filters.status,
    source: filters.source,
    archive: filters.archive,
    sort: filters.sort,
  })

  // a short wait, so typing a price or a floor sends one request
  useEffect(() => {
    if (!settingsLoaded || !referencesDone) return undefined
    requestId.current += 1
    const id = requestId.current
    const wait = setTimeout(() => {
      setLoading(true)
      ApiApartmentsService.fetchList({...listParams(), fields: fields.join(','), page: page.current, limit: page.size})
        .then(({data: list}) => {
          if (id !== requestId.current) return
          setData(list)
          setError(null)
        })
        .catch(() => id === requestId.current && setError('Не удалось загрузить квартиры. Обновите страницу.'))
        .finally(() => id === requestId.current && setLoading(false))
    }, 250)
    return () => clearTimeout(wait)
    // the portal's filters are chosen only after their config has come, so kinds is never behind them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, portalFilters, page, reloadTick, fields.join(','), settingsLoaded, referencesDone])

  // the analogs come from the portal's search (its order: the closest price first), their lines from this list
  const single = data.total === 1 && data.items.length === 1 ? data.items[0].id : null
  useEffect(() => {
    setAnalogs(null)
    if (!single) return undefined
    let gone = false
    ApiApartmentsService.fetchAnalogs(single)
      .then(async ({data: found}) => {
        const blocks = [['inComplex', 'Аналоги в ЖК'], ['nearby', 'Аналоги поблизости']]
          .map(([key, title]) => ({key, title, ids: ((found && found[key]) || []).map((flat) => flat.id)}))
        const ids = [...new Set(blocks.flatMap((block) => block.ids))]
        if (!ids.length) return gone ? null : setAnalogs([])
        const {data: list} = await ApiApartmentsService.fetchList({q: ids.join(' '), archive: 'all', fields: fields.join(','), page: 1, limit: ids.length + 20})
        const byId = new Map((list.items || []).map((row) => [row.id, row]))
        if (!gone) setAnalogs(blocks.map((block) => ({...block, rows: block.ids.map((id) => byId.get(id)).filter(Boolean)})).filter((block) => block.rows.length))
      })
      .catch(() => !gone && setAnalogs([]))
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, reloadTick, fields.join(',')])

  // other filters, other flats: the choice for «Массовая правка» starts again
  useEffect(() => {
    setSelected([])
  }, [filters.q, filters.status, filters.source, filters.archive, portalFilters])

  // the left-right bar under the column names (owner 03.11: «бегунок в таблице», not only at the bottom): the sticky header
  // scrolls itself (AdminPage.scss), the rows follow it; antd moves the header with the rows, not the rows with the header
  useEffect(() => {
    const follow = (event) => {
      const header = event.target
      if (!header.classList || !header.classList.contains('ant-table-header') || !header.closest('.flats-table')) return
      const body = header.parentElement && header.parentElement.querySelector('.ant-table-body')
      if (body && body.scrollLeft !== header.scrollLeft) body.scrollLeft = header.scrollLeft
    }
    document.addEventListener('scroll', follow, true)
    return () => document.removeEventListener('scroll', follow, true)
  }, [])

  const remember = (next) => {
    setSettings(next)
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      ApiUserSettingsService.set(SETTINGS_KEY, next).catch(() => writeLocal(next.columns))
    }, 600)
  }

  const setFilter = (values) => {
    setFilters((current) => ({...current, ...values}))
    setPage((current) => ({...current, current: 1}))
  }

  const setPortalFilter = (key, value) => {
    setPortalFilters((current) => {
      const next = {...current}
      if (value === undefined) delete next[key]
      else next[key] = value
      return next
    })
    setPage((current) => ({...current, current: 1}))
  }

  const resetPortalFilters = () => {
    setPortalFilters({})
    setPage((current) => ({...current, current: 1}))
  }

  const onSearch = (value) => {
    setSearch(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setFilter({q: value.trim()}), 400)
  }

  const reload = () => setReloadTick((n) => n + 1)

  // «В Запас» / «Из Запаса» right from the table (owner 03.11), for the roles with «Запас» (they have its filter): ECI old
  // saves it first; the flats it refuses are named
  const canStock = canEdit && Boolean(kinds.stock)
  const moveStock = async (ids, stock) => {
    try {
      const {data} = await ApiApartmentsService.setStock(ids, stock)
      if (data.updated) message.success(`${stock ? 'В Запас' : 'Из Запаса'}: ${flatsText(data.updated)}`)
      if (data.refused && data.refused.length) {
        Modal.warning({
          title: `Старый сайт не принял: ${flatsText(data.refused.length)}`,
          content: <ul className='old-errors'>{data.refused.slice(0, 20).map((r) => <li key={r.id}>№ {r.eciOldId}: {r.error}</li>)}</ul>,
        })
      }
      setSelected((list) => list.filter((id) => !ids.includes(id)))
      reload()
    } catch (error) {
      message.error(error?.response?.data?.errors?.message || 'Не удалось изменить Запас')
    }
  }

  // a click on a column's header sorts by it; a third click returns to the newest first
  const onTableChange = (pagination, tableFilters, sorter) => {
    if (!sorter || Array.isArray(sorter) || !sorter.columnKey) return
    const key = sortKeyOf(sorter.columnKey, catalogue)
    if (!key) return
    const next = sorter.order === 'ascend' ? key : sorter.order === 'descend' ? `-${key}` : 'id'
    if (next !== filters.sort) setFilter({sort: next})
  }

  // a cell's field, when this user may change it there: { field, list }
  const fieldByKey = useMemo(() => Object.fromEntries((references ? references.meta.groups : []).flatMap((g) => g.fields).map((f) => [f.key, f])),
    [references])
  const editableSet = useMemo(() => new Set(references ? references.meta.editable : []), [references])
  const editableOf = (key) => {
    const field = fieldByKey[key]
    if (!field || !editableSet.has(key) || !EDIT_KINDS.includes(field.kind) || CARD_ONLY.includes(key)) return null
    return {field, list: references.meta.lists[key]}
  }

  const commitCell = async (id, field, value) => {
    setSavingCell(true)
    try {
      const {data: result} = await ApiApartmentsService.updateApartment(id, {[field]: value})
      const saved = result.apartment || {}
      setData((current) => ({...current, items: current.items.map((row) => (row.id === id ? mergeSaved(row, saved) : row))}))
      setEditingCell(null)
      message.success('Сохранено', 1)
    } catch (failure) {
      message.error(failure?.response?.data?.errors?.message || 'Не удалось сохранить')
    } finally {
      setSavingCell(false)
    }
  }

  const cell = {
    editing: editingCell,
    saving: savingCell,
    references,
    editableOf,
    start: (id, field) => {
      clearTimeout(clickTimer.current)
      setEditingCell({id, field})
    },
    commit: commitCell,
    cancel: () => setEditingCell(null),
  }

  // a click on a line opens the flat, but not on its links, buttons, the photo, the ticks or a cell being changed; with the
  // right to change, a moment later — a second click makes a double click, which changes the cell
  const ignored = (event) => !event.currentTarget.contains(event.target)
    || event.target.closest('a, button, .ant-popover, img, .flat-cell-editor, .ant-table-selection-column')
  const onRow = (row) => ({
    onClick: (event) => {
      if (ignored(event)) return
      clearTimeout(clickTimer.current)
      clickTimer.current = setTimeout(() => setEditing(row.id), canEdit ? 250 : 0)
    },
    onDoubleClick: (event) => {
      if (ignored(event)) return
      clearTimeout(clickTimer.current)
      setEditing(row.id)
    },
  })

  const selectAllFound = async () => {
    try {
      const {data: found} = await ApiApartmentsService.fetchIds(listParams())
      setSelected(found.ids)
      if (found.more) message.warning(`Выбраны первые ${flatsText(found.ids.length)}: больше за один раз нельзя`)
    } catch (failure) {
      message.error('Не удалось выбрать квартиры')
    }
  }

  const sortOptions = SORT_PRESETS.some((o) => o.value === filters.sort)
    ? SORT_PRESETS
    : [...SORT_PRESETS, {value: filters.sort, label: 'По колонке таблицы'}]

  const columns = tableColumns({shown: settings.columns, catalogue, sort: filters.sort, canEdit, onEdit: setEditing, cell,
    onStock: canStock ? (row) => moveStock([row.id], !row.stock) : null})
  const rowClassName = (row) => [row.stock && 'flats-table__row--stock', row.archived && 'flats-table__row--archived'].filter(Boolean).join(' ')

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message={references?.meta?.eciOldSave === 'live'
          ? 'Двойной щелчок по ячейке меняет её, не открывая квартиру. Квартиры со старого сайта сохраняются сначала там — как кнопкой «Сохранить» на старом сайте; если он не принял правку, она не сохраняется и здесь.'
          : 'Двойной щелчок по ячейке меняет её, не открывая квартиру. Квартиры со старого сайта можно править и здесь, но старый сайт главнее: правка продержится до следующего изменения квартиры там или до ночной сверки. На старый сайт отсюда ничего не уходит.'}
      />
      {error && <Alert className='section-note' type='error' message={error}/>}
      <FiltersPanel
        config={filterConfig}
        facets={facets}
        lists={references?.meta?.lists}
        values={portalFilters}
        onChange={setPortalFilter}
        onReset={resetPortalFilters}
        search={search}
        onSearch={onSearch}
        onAll={() => setFiltersOpen(true)}
        total={data.total}
      />
      <Table
        title={() => (
          <>
            <SectionToolbar
              count={`Всего ${data.total.toLocaleString('ru-RU')}`}
              filters={(
                <>
                  <TickedSelect style={{width: 170}} placeholder='Любой статус' aria-label='Статус' options={STATUS_OPTIONS}
                    value={filters.status} onChange={(value) => setFilter({status: value})}/>
                  <Select allowClear style={{width: 200}} placeholder='Все квартиры' value={filters.source} onChange={(value) => setFilter({source: value})}>
                    <Select.Option value='eci_old'>Со старого сайта</Select.Option>
                    <Select.Option value='manual'>Созданные здесь</Select.Option>
                  </Select>
                  <Select style={{width: 150}} value={filters.archive || 'without'} options={ARCHIVE_OPTIONS} aria-label='Архив'
                    onChange={(value) => setFilter({archive: value === 'without' ? undefined : value})}/>
                  {kinds.stock && (
                    <Select style={{width: 150}} value={portalFilters.stock ? 'only' : 'without'} options={STOCK_OPTIONS} aria-label='Запас'
                      className={portalFilters.stock ? 'flat-filters__select--active' : undefined}
                      onChange={(value) => setPortalFilter('stock', value === 'only' ? true : undefined)}/>
                  )}
                  <Select style={{width: 190}} value={filters.sort} options={sortOptions} onChange={(value) => setFilter({sort: value})}/>
                  <ColumnsButton onClick={() => setColumnsOpen(true)}/>
                </>
              )}
            >
              {canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => setEditing('new')}>Добавить квартиру</Button>}
            </SectionToolbar>
            {canEdit && selected.length > 0 && (
              <div className='flats-selection'>
                <b>Выбрано: {flatsText(selected.length)}</b>
                {selected.length < data.total && (
                  <Button type='link' onClick={selectAllFound}>Выбрать все найденные ({data.total.toLocaleString('ru-RU')})</Button>
                )}
                <Button type='primary' icon={<EditOutlined/>} disabled={!references} onClick={() => setBulkOpen(true)}>Изменить поле у выбранных</Button>
                {canStock && (
                  <>
                    <Popconfirm title={`Отправить в Запас: ${flatsText(selected.length)}?`} okText='В Запас' cancelText='Отмена'
                      onConfirm={() => moveStock(selected, true)}>
                      <Button icon={<InboxOutlined/>}>В Запас</Button>
                    </Popconfirm>
                    <Popconfirm title={`Достать из Запаса: ${flatsText(selected.length)}?`} okText='Из Запаса' cancelText='Отмена'
                      onConfirm={() => moveStock(selected, false)}>
                      <Button icon={<ExportOutlined rotate={180}/>}>Из Запаса</Button>
                    </Popconfirm>
                  </>
                )}
                <Button type='link' onClick={() => setSelected([])}>Снять выбор</Button>
              </div>
            )}
          </>
        )}
        className='flats-table'
        size='small'
        sticky
        columns={columns}
        dataSource={data.items}
        rowKey='id'
        rowSelection={canEdit ? {
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys),
          preserveSelectedRowKeys: true,
          fixed: true,
          columnWidth: 32,
        } : undefined}
        onRow={onRow}
        // Запас: orange; ECI old's archive (not on the portal): pale (owner 03.11)
        rowClassName={rowClassName}
        onChange={onTableChange}
        loading={loading}
        locale={{emptyText: 'Ничего не нашлось'}}
        scroll={{x: 'max-content'}}
        pagination={{
          current: page.current,
          pageSize: page.size,
          total: data.total,
          showSizeChanger: true,
          pageSizeOptions: ['50', '100', '200'],
          locale: {items_per_page: '/ стр.'},
          showTotal: (total, [from, to]) => `${from}–${to} из ${total}`,
          onChange: (current, size) => setPage({current, size}),
        }}
      />
      {(analogs || []).map((block) => (
        <section key={block.key} className='flats-analogs'>
          <h3 className='flats-analogs__title'>{block.title} <span className='flats-analogs__count'>{block.rows.length}</span></h3>
          <Table
            className='flats-table'
            size='small'
            columns={columns}
            dataSource={block.rows}
            rowKey='id'
            onRow={onRow}
            rowClassName={rowClassName}
            scroll={{x: 'max-content'}}
            pagination={false}
          />
        </section>
      ))}
      <AllFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        config={filterConfig}
        facets={facets}
        lists={references?.meta?.lists}
        values={portalFilters}
        onChange={setPortalFilter}
        total={data.total}
      />
      <ColumnsDrawer
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        catalogue={catalogue}
        shown={settings.columns}
        onChange={(columns) => remember({...settings, columns})}
      />
      {bulkOpen && references && (
        <BulkEditModal
          open
          ids={selected}
          references={references}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false)
            setSelected([])
            reload()
          }}
        />
      )}
      {editing !== null && (
        <ApartmentEditor
          key={editing}
          apartmentId={editing}
          complexes={complexes}
          readOnly={!canEdit}
          onClose={() => setEditing(null)}
          onSaved={reload}
          onCreated={(apartment) => {
            reload()
            setEditing(apartment.id)
          }}
          onDeleted={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </>
  )
}
